import { useState, useCallback, useEffect, useRef } from "react";
import { request } from "@/Api";

/**
 * useThreadManager — manages thread list, selection, and workflow switching.
 *
 * Encapsulates:
 *  - Fetching threads (scoped by modelType/modelInstanceId)
 *  - Active thread selection
 *  - Active agent type (derived from thread or manually set)
 *  - Workflow switching (triggers file uploader, resets state)
 *  - Auto-start on mount when initialAgent is provided
 *
 * @param {Object} options
 * @param {string|null} options.modelType - "project" or null for global
 * @param {number|null} options.modelInstanceId - e.g. projectId
 * @param {string|null} options.initialAgent - auto-open with this agent type
 * @param {Function} options.onResetWidgets - callback to reset workflow-specific state
 */
export default function useThreadManager({
    modelType,
    modelInstanceId,
    initialAgent,
    onResetWidgets,
}) {
    const [threads, setThreads] = useState([]);
    const [threadsLoading, setThreadsLoading] = useState(true);
    const [activeThread, setActiveThread] = useState(null);
    const [activeAgentType, setActiveAgentType] = useState(
        initialAgent || "router",
    );
    const [showFileUploader, setShowFileUploader] = useState(false);

    // ─── Fetch threads ──────────────────────────────────────────────────
    const fetchThreads = useCallback(() => {
        setThreadsLoading(true);
        const endpoint = modelType
            ? `/threads/by-model?model_type=${modelType}&model_id=${modelInstanceId}`
            : "/threads";

        request
            .get(endpoint)
            .then((response) => {
                setThreads(response.data);
            })
            .catch((error) => {
                console.error("Failed to fetch threads:", error);
            })
            .finally(() => {
                setThreadsLoading(false);
            });
    }, [modelType, modelInstanceId]);

    useEffect(() => {
        fetchThreads();
    }, [fetchThreads]);

    // ─── Auto-start workflow on mount ───────────────────────────────────
    const initialSetupDone = useRef(false);
    useEffect(() => {
        if (initialSetupDone.current) return;
        if (threadsLoading) return;
        initialSetupDone.current = true;

        if (initialAgent && initialAgent !== "router") {
            setActiveAgentType(initialAgent);
        }
    }, [threadsLoading, initialAgent, threads]);

    // ─── Thread selection ───────────────────────────────────────────────
    const selectThread = useCallback(
        (thread) => {
            setActiveThread(thread);
            setActiveAgentType(thread.agent_type || "router");
            setShowFileUploader(false);
            onResetWidgets?.();
        },
        [onResetWidgets],
    );

    const startNewChat = useCallback(() => {
        setActiveThread(null);
        setActiveAgentType("router");
        setShowFileUploader(false);
        onResetWidgets?.();
    }, [onResetWidgets]);

    const startNewWorkflow = useCallback(
        (agentType) => {
            setActiveThread(null);
            setActiveAgentType(agentType);
            onResetWidgets?.();
        },
        [onResetWidgets],
    );

    const handleNewThreadCreated = useCallback(
        (threadId) => {
            fetchThreads();
            // Store the thread ID so it survives layout transitions (e.g. intake chat-only → canvas split)
            if (threadId && !activeThread) {
                setActiveThread({ id: threadId });
            }
        },
        [fetchThreads, activeThread],
    );

    return {
        threads,
        threadsLoading,
        activeThread,
        activeAgentType,
        showFileUploader,
        setShowFileUploader,
        selectThread,
        startNewChat,
        startNewWorkflow,
        handleNewThreadCreated,
        refreshThreads: fetchThreads,
    };
}
